import { useState } from "react";
import { Spin, Typography } from "antd";
import { useMediaQuery } from 'react-responsive';
import { EuroCircleOutlined } from "@ant-design/icons";
import { useAuth } from "../../hooks/AuthContext";
import { ModaleConfirmSuppressionInscription } from "../../components/modals/ModalConfirmSuppressionInscription";
import exportToExcel, { ExcelColumnHeadersType } from "../../utils/FormUtils";
import { AdhesionLight } from "../../services/adhesion";
import { useAdhesionManagement } from "./adhesion/hooks/useAdhesionManagement";
import { AdhesionMobileView } from "./adhesion/AdhesionMobileView";
import { AdhesionDesktopView } from "./adhesion/AdhesionDesktopView";
import { UnahtorizedAccess } from "../public/UnahtorizedAccess";
import { BatchLockAlert } from "../../components/common/BatchLockAlert";

export const AdminAdhesion = () => {
    const [modaleConfirmSuppressionOpen, setModaleConfirmSuppressionOpen] = useState<boolean>(false);
    const isMobile = useMediaQuery({ maxWidth: 768 });
    const { roles } = useAuth();

    const {
        dataSource,
        selectedAdhesions,
        setSelectedAdhesions,
        isLoading,
        searchAdhesions,
        validateAdhesion,
        validateAdhesions,
        deleteAdhesion,
        deleteAdhesions,
        batchLockConflict,
        setBatchLockConflict,
    } = useAdhesionManagement();

    const excelColumnHeaders: ExcelColumnHeadersType<AdhesionLight> = {
        nom: "Nom",
        prenom: "Prénom",
        ville: "Ville",
        montant: "Montant",
        statut: "Statut",
        dateInscription: "Date adhésion",
    };

    const exportData = () => {
        if (dataSource) {
            exportToExcel<AdhesionLight>(dataSource, excelColumnHeaders, `adhesion.xlsx`);
        }
    };

    const handleDeleteAdhesions = async () => {
        setModaleConfirmSuppressionOpen(false);
        await deleteAdhesions(selectedAdhesions);
    };

    return roles?.includes("ROLE_ADMIN") || roles?.includes("ROLE_TRESORIER") ? (
        <div className="centered-content">
            <div className="container-full-width">
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '16px', paddingBottom: '16px', borderBottom: '3px solid #13c2c2', marginBottom: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '48px', height: '48px', borderRadius: '10px', backgroundColor: '#13c2c2', flexShrink: 0 }}>
                        <EuroCircleOutlined style={{ fontSize: '24px', color: '#ffffff' }} />
                    </div>
                    <div>
                        <Typography.Title level={3} style={{ margin: 0, lineHeight: 1.2 }}>
                            Administration des adhésions
                        </Typography.Title>
                        <Typography.Text type="secondary">
                            Gestion et suivi des adhésions
                        </Typography.Text>
                    </div>
                </div>

                <BatchLockAlert
                    show={batchLockConflict.show}
                    resourceType={batchLockConflict.resourceType}
                    username={batchLockConflict.username}
                    expiresAt={batchLockConflict.expiresAt}
                />

                <Spin spinning={isLoading}>
                    <div className="search-result-container">
                        {isMobile ? (
                            <AdhesionMobileView
                                dataSource={dataSource}
                                selectedAdhesions={selectedAdhesions}
                                setSelectedAdhesions={setSelectedAdhesions}
                                onValidateAdhesion={validateAdhesion}
                                onValidateAdhesions={validateAdhesions}
                                onDeleteAdhesion={deleteAdhesion}
                                onDeleteAdhesions={async (adhesions) => { setModaleConfirmSuppressionOpen(true); }}
                                onSearch={searchAdhesions}
                                onExport={exportData}
                            />
                        ) : (
                            <AdhesionDesktopView
                                dataSource={dataSource}
                                selectedAdhesions={selectedAdhesions}
                                setSelectedAdhesions={setSelectedAdhesions}
                                onValidateAdhesion={validateAdhesion}
                                onValidateAdhesions={validateAdhesions}
                                onDeleteAdhesion={deleteAdhesion}
                                onDeleteAdhesions={async (adhesions) => { setModaleConfirmSuppressionOpen(true); }}
                                onSearch={searchAdhesions}
                                onExport={exportData}
                            />
                        )}
                    </div>

                    <ModaleConfirmSuppressionInscription
                        open={modaleConfirmSuppressionOpen}
                        setOpen={setModaleConfirmSuppressionOpen}
                        nbInscriptions={selectedAdhesions.length}
                        onConfirm={handleDeleteAdhesions}
                    />
                </Spin>
            </div>
        </div>
    ) : <UnahtorizedAccess />;
}